import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import List from "@mui/material/List";
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

function SidenavCollapse({
  icon,
  name,
  route,
  collapse,
  active,
  openItem,
  setOpenItem,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavColor,
  } = controller;

  // Check if this dropdown is open
  const isOpen = openItem === name;

  // Toggle function to open/close this dropdown
  const toggleCollapse = () => {
    setOpenItem(isOpen ? null : name);
  };

  // If has collapse array → dropdown
  if (collapse) {
    return (
      <>
        <ListItem component="li">
          <MDBox
            {...rest}
            onClick={toggleCollapse}
            sx={(theme) =>
              collapseItem(theme, {
                active,
                transparentSidenav,
                whiteSidenav,
                darkMode,
                sidenavColor,
              })
            }
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <MDBox display="flex" alignItems="center">
              <ListItemIcon
                sx={(theme) =>
                  collapseIconBox(theme, {
                    transparentSidenav,
                    whiteSidenav,
                    darkMode,
                    active,
                  })
                }
              >
                {typeof icon === "string" ? (
                  <Icon sx={(theme) => collapseIcon(theme, { active })}>
                    {icon}
                  </Icon>
                ) : (
                  icon
                )}
              </ListItemIcon>

              <ListItemText
                primary={name}
                sx={(theme) => ({
                  ...collapseText(theme, {
                    miniSidenav,
                    transparentSidenav,
                    whiteSidenav,
                    active,
                  }),
                  color: "#fff", // Always white text
                })}
              />
            </MDBox>

            {/* Arrow icon */}
            <Icon sx={{ fontSize: "20px", color: "#fff" }}>
              {isOpen ? "expand_more" : "chevron_right"}
            </Icon>
          </MDBox>
        </ListItem>

        {/* Dropdown content */}
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {collapse.map((item) => (
              <SidenavCollapse
                key={item.key}
                name={item.name}
                icon={item.icon || <Icon />}
                route={item.route}
                collapse={item.collapse}
                active={item.key === active}
                openItem={openItem}
                setOpenItem={setOpenItem}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  // Normal navigation (no dropdown)
  return (
    <ListItem component="li">
      <NavLink to={route} style={{ textDecoration: "none", width: "100%" }}>
        <MDBox
          {...rest}
          sx={(theme) =>
            collapseItem(theme, {
              active,
              transparentSidenav,
              whiteSidenav,
              darkMode,
              sidenavColor,
            })
          }
        >
          <ListItemIcon
            sx={(theme) =>
              collapseIconBox(theme, {
                transparentSidenav,
                whiteSidenav,
                darkMode,
                active,
              })
            }
          >
            {typeof icon === "string" ? (
              <Icon sx={(theme) => collapseIcon(theme, { active })}>
                {icon}
              </Icon>
            ) : (
              icon
            )}
          </ListItemIcon>
          <ListItemText
            primary={name}
            sx={(theme) => ({
              ...collapseText(theme, {
                miniSidenav,
                transparentSidenav,
                whiteSidenav,
                active,
              }),
              color: "#fff", // Always white text
            })}
          />
        </MDBox>
      </NavLink>
    </ListItem>
  );
}

SidenavCollapse.defaultProps = {
  active: false,
  collapse: null,
  openItem: null,
  setOpenItem: () => {},
};

SidenavCollapse.propTypes = {
  icon: PropTypes.node,
  name: PropTypes.string.isRequired,
  route: PropTypes.string,
  collapse: PropTypes.arrayOf(PropTypes.object),
  active: PropTypes.bool,
  openItem: PropTypes.string,
  setOpenItem: PropTypes.func,
};

export default SidenavCollapse;
